import { useState, useEffect, type FormEvent } from "react";
import { Link } from "wouter";
import { Phone, MapPin, Clock, Wrench, ShieldCheck, CheckCircle2, Star, Menu, X, ChevronRight, ArrowRight, Loader2, AlertCircle, Trophy, type LucideProps } from "lucide-react";
import WhatsAppButton from "@/components/WhatsAppButton";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const planIconMap: Record<string, React.ComponentType<LucideProps>> = {
  Wrench,
  ShieldCheck,
  Trophy,
};

export default function Home() {
  const { config: cfg, images: imgs, plans } = useSiteConfig();
  const hasPersistentHero = typeof document !== "undefined" && document.getElementById("persistent-home-hero") !== null;
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const values = new FormData(formElement);
    setIsSubmitting(true);
    setHasError(false);
    try {
      const { submitWebsiteForm } = await import("@/lib/submit-form");
      await submitWebsiteForm({
          _subject: "New Quote Request – Swift Mobile Mechanic",
          form_name: "Home Page Quick Quote",
          full_name: String(values.get("name") ?? ""),
          phone_number: String(values.get("phone") ?? ""),
          location: String(values.get("location") ?? ""),
          vehicle_details: String(values.get("vehicle") ?? ""),
          issue_description: String(values.get("issue") ?? ""),
          page_url: window.location.href,
          submission_timestamp: new Date().toLocaleString("en-CA", { timeZone: "America/Winnipeg" }),
      });
      setIsSuccess(true);
      formElement.reset();
      setTimeout(() => setIsSuccess(false), 10000);
    } catch {
      setHasError(true);
    } finally {
      setIsSubmitting(false);
    }
  }

  const scrollToSection = (id: string) => {
    setMobileMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="min-h-screen bg-transparent text-foreground font-sans selection:bg-primary selection:text-primary-foreground">
      {/* 1. Utility Bar */}
      <div className="bg-popover border-b border-border text-muted-foreground text-xs py-2 px-4 md:px-8 hidden md:flex justify-between items-center z-50 relative">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-primary" /> Serving Winnipeg & Surrounding Areas</span>
          <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-primary" /> Available {cfg.hours}</span>
        </div>
        <div className="flex items-center gap-2 font-medium text-foreground tracking-wide">
          <Phone className="w-3.5 h-3.5 text-primary" /> {cfg.phone.display}
        </div>
      </div>

      {/* 2. Sticky Header */}
      <header className={`sticky top-0 w-full z-40 transition-all duration-300 border-b ${isScrolled ? "bg-background/95 backdrop-blur-md border-border shadow-sm py-3" : "bg-background border-transparent py-5"}`}>
        <div className="container mx-auto px-4 md:px-8 flex justify-between items-center">
          <button type="button" aria-label="Back to top" className="flex items-center gap-2 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <div className="w-8 h-8 rounded bg-primary flex items-center justify-center">
              <Wrench className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-bold text-xl tracking-tight uppercase">Swift <span className="text-primary">Mobile Mechanic</span></span>
          </button>

          <nav className="hidden lg:flex items-center gap-8 text-sm font-medium text-muted-foreground">
            <Link href="/services" className="hover:text-primary transition-colors">Services</Link>
            <button onClick={() => scrollToSection('how-it-works')} className="hover:text-primary transition-colors">How it Works</button>
            <Link href="/about" className="hover:text-primary transition-colors">About</Link>
            <Link href="/book" className="hover:text-primary transition-colors">Book</Link>
            <button onClick={() => scrollToSection('reviews')} className="hover:text-primary transition-colors">Reviews</button>
            <Link href="/contact" className="hover:text-primary transition-colors">Contact</Link>
            <Button asChild size="sm" className="font-semibold tracking-wide">
              <Link href="/book">Book Mobile Service</Link>
            </Button>
          </nav>

          <button className="lg:hidden text-foreground" type="button" aria-label={mobileMenuOpen ? "Close mobile navigation menu" : "Open mobile navigation menu"} aria-expanded={mobileMenuOpen} onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="mobile-menu-enter fixed inset-0 top-[70px] z-30 bg-background/98 backdrop-blur-xl border-b border-border p-4 flex flex-col gap-6 lg:hidden">
          <nav className="flex flex-col gap-4 text-lg font-medium">
            <Link href="/services" className="flex items-center justify-between p-4 bg-popover rounded-lg border border-border">Services <ChevronRight className="w-5 h-5 text-primary" /></Link>
            <button onClick={() => scrollToSection('how-it-works')} className="flex items-center justify-between p-4 bg-popover rounded-lg border border-border">How it Works <ChevronRight className="w-5 h-5 text-primary" /></button>
            <Link href="/about" className="flex items-center justify-between p-4 bg-popover rounded-lg border border-border">About <ChevronRight className="w-5 h-5 text-primary" /></Link>
            <Link href="/book" className="flex items-center justify-between p-4 bg-popover rounded-lg border border-border">Book <ChevronRight className="w-5 h-5 text-primary" /></Link>
            <button onClick={() => scrollToSection('reviews')} className="flex items-center justify-between p-4 bg-popover rounded-lg border border-border">Reviews <ChevronRight className="w-5 h-5 text-primary" /></button>
            <Link href="/contact" className="flex items-center justify-between p-4 bg-popover rounded-lg border border-border">Contact <ChevronRight className="w-5 h-5 text-primary" /></Link>
          </nav>
          <Button asChild size="lg" className="w-full mt-auto text-lg">
            <Link href="/book">Book Mobile Service</Link>
          </Button>
        </div>
      )}

      {/* 3. Hero */}
      <section className="relative pt-20 pb-32 lg:pt-32 lg:pb-40 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/85 to-background/30 z-10" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent z-10" />
          {!hasPersistentHero && (
            <picture className="block w-full h-full">
              <source media="(max-width: 767px)" srcSet={imgs.vanMobile || imgs.van} />
              <source media="(min-width: 768px)" srcSet={imgs.vanDesktop || imgs.van} />
              <img
                src={imgs.vanDesktop || imgs.van}
                alt="Swift Mobile Mechanic branded service van at customer location in Winnipeg"
                width={1600}
                height={1200}
                loading="eager"
                fetchPriority="high"
                className="w-full h-full object-cover object-center opacity-55"
              />
            </picture>
          )}
        </div>

        <div className="container mx-auto px-4 md:px-8 relative z-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-popover border border-border text-xs font-medium text-muted-foreground mb-6 uppercase tracking-wider">
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              Winnipeg's Mobile Mechanic
            </div>
            
            <p className="text-primary font-bold text-sm tracking-[0.2em] uppercase mb-3">Your Garage on Wheels — We Come to You</p>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.1] mb-6 text-foreground">
              Skip the Shop. <br />
              <span className="text-primary">Book a Trusted Mobile Mechanic.</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-muted-foreground mb-10 max-w-2xl leading-relaxed font-light">
              No towing. No waiting rooms. Professional, honest car repair performed on-site at your home, workplace, or roadside.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Button onClick={() => scrollToSection('contact')} size="lg" className="h-14 px-8 text-base md:text-lg shadow-[0_0_40px_-10px_hsl(var(--primary))] hover:shadow-[0_0_60px_-15px_hsl(var(--primary))] transition-shadow group">
                Get a Fast Quote <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full h-14 px-8 text-base md:text-lg border-muted-foreground/30 hover:bg-popover">
                <Link href="/services">View Services</Link>
              </Button>
            </div>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-muted-foreground">
              <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary" /> Serving Winnipeg & Area</span>
              <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary" /> Transparent Pricing</span>
              <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary" /> Honest, No-Pressure</span>
              <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary" /> We accept debit and credit cards</span>
            </div>
          </div>
        </div>
      </section>

      {/* 4. Trust Bar */}
      <div className="border-y border-border bg-popover/50">
        <div className="container mx-auto px-4 md:px-8 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center md:text-left divide-x divide-border/0 md:divide-border">
            <div className="flex flex-col md:flex-row items-center md:justify-center gap-3 px-4">
              <MapPin className="w-6 h-6 text-primary opacity-80" />
              <span className="text-sm font-medium tracking-wide uppercase text-muted-foreground">We come to you</span>
            </div>
            <div className="flex flex-col md:flex-row items-center md:justify-center gap-3 px-4">
              <ShieldCheck className="w-6 h-6 text-primary opacity-80" />
              <span className="text-sm font-medium tracking-wide uppercase text-muted-foreground">Transparent pricing</span>
            </div>
            <div className="flex flex-col md:flex-row items-center md:justify-center gap-3 px-4">
              <MapPin className="w-6 h-6 text-primary opacity-80" />
              <span className="text-sm font-medium tracking-wide uppercase text-muted-foreground">Winnipeg & area</span>
            </div>
            <div className="flex flex-col md:flex-row items-center md:justify-center gap-3 px-4">
              <Star className="w-6 h-6 text-primary opacity-80" />
              <span className="text-sm font-medium tracking-wide uppercase text-muted-foreground">Honest service</span>
            </div>
          </div>
        </div>
      </div>

      {/* 5. Featured Services */}
      <section id="services" className="scroll-mt-24 py-24 bg-background">
        <div className="container mx-auto px-4 md:px-8">
          <div className="mb-16 md:flex md:justify-between md:items-end">
            <div className="max-w-2xl">
              <p className="text-sm font-bold tracking-[0.2em] text-primary uppercase mb-3">Our Expertise</p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">Complete Car Care. <br className="hidden md:block" />Delivered to Your Driveway.</h2>
              <p className="text-muted-foreground text-lg">We bring the shop to you, fully equipped to handle 80% of standard repairs and maintenance.</p>
            </div>
            <Button asChild variant="outline" className="hidden md:inline-flex mt-6 md:mt-0">
              <Link href="/services">View Full Service List</Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: "Vehicle Diagnostics", desc: "Check engine light on? We use professional scanners to identify the exact issue without the shop markup." },
              { title: "Brake Repair", desc: "Squeaking or grinding? We replace pads, rotors, and calipers safely at your location." },
              { title: "Battery & No-Start", desc: "Car won't turn over? We test alternators, starters, and replace dead batteries on the spot." },
              { title: "Oil Change & Fluids", desc: "Premium synthetic oil changes and fluid top-ups performed cleanly on your driveway." },
              { title: "Pre-Purchase Inspections", desc: "Buying a used car? Let us inspect it before you hand over the cash. Detailed report included." },
              { title: "Emergency Roadside", desc: "Stuck on the side of the road? We offer quick response times for minor roadside mechanical failures." }
            ].map((service, i) => (
              <div key={i} className="group relative p-8 bg-card border border-card-border rounded-xl hover:border-primary/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-primary/5 overflow-hidden">
                <div className="absolute top-0 right-0 p-6 opacity-5 group-hover:opacity-10 transition-opacity transform group-hover:scale-110 duration-500 pointer-events-none">
                  <Wrench className="w-32 h-32 text-foreground" />
                </div>
                <div className="w-12 h-12 bg-popover border border-border rounded-lg flex items-center justify-center mb-6 text-primary group-hover:scale-110 transition-transform duration-300">
                  <Wrench className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold mb-3">{service.title}</h3>
                <p className="text-muted-foreground leading-relaxed relative z-10">{service.desc}</p>
              </div>
            ))}
          </div>
          <Button asChild variant="outline" className="w-full mt-8 md:hidden">
            <Link href="/services">View Full Service List</Link>
          </Button>
        </div>
      </section>

      {/* 6. How it Works */}
      <section id="how-it-works" className="scroll-mt-24 py-24 bg-popover border-y border-border">
        <div className="container mx-auto px-4 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <p className="text-sm font-bold tracking-[0.2em] text-primary uppercase mb-3">The Process</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">Fixing Your Car is Now Effortless.</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-12 relative">
            <div className="hidden md:block absolute top-12 left-1/6 right-1/6 h-[1px] bg-gradient-to-r from-transparent via-border to-transparent -z-10" />
            
            {[
              { num: "01", title: "Tell Us What's Happening", desc: "Fill out our quick form or call us. Tell us your vehicle details and the symptoms." },
              { num: "02", title: "We Come to Your Location", desc: "We arrive at your home, office, or roadside with a fully equipped mobile repair van." },
              { num: "03", title: "We Fix It On-Site", desc: "We perform the repair efficiently, cleanly, and honestly. You only pay for what you need." }
            ].map((step, i) => (
              <div key={i} className="relative text-center">
                <div className="w-24 h-24 mx-auto bg-background border border-border rounded-full flex items-center justify-center mb-8 shadow-sm relative z-10">
                  <span className="text-3xl font-black text-primary/80">{step.num}</span>
                </div>
                <h3 className="text-xl font-bold mb-4">{step.title}</h3>
                <p className="text-muted-foreground leading-relaxed max-w-sm mx-auto">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. Why Choose Us */}
      <section id="why-us" className="py-24 bg-background">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-sm font-bold tracking-[0.2em] text-primary uppercase mb-3">The Swift Advantage</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-8 leading-tight">The Dealership Alternative You've Been Looking For.</h2>
              
              <div className="space-y-8">
                <div className="flex gap-4">
                  <div className="mt-1"><CheckCircle2 className="w-6 h-6 text-primary" /></div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Zero Towing, Zero Waiting</h3>
                    <p className="text-muted-foreground">Why pay for a tow truck and sit in a dingy waiting room? We turn your driveway into the service bay.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="mt-1"><CheckCircle2 className="w-6 h-6 text-primary" /></div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Honest, Transparent Pricing</h3>
                    <p className="text-muted-foreground">No hidden shop fees, no aggressive service advisors trying to upsell you on cabin air filters. We quote exactly what it takes to fix the problem.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="mt-1"><CheckCircle2 className="w-6 h-6 text-primary" /></div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Premium Workmanship</h3>
                    <p className="text-muted-foreground">We use high-quality OEM or equivalent parts, backed by professional experience and modern diagnostic tools.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute inset-0 bg-primary/20 blur-3xl -z-10 rounded-full" />
              <img 
                src={imgs.engineBayOptimized || imgs.engineBay}
                alt="On-site engine service performed by Swift Mobile Mechanic"
                loading="lazy"
                width={800}
                height={600}
                className="w-full h-auto rounded-2xl border border-border shadow-2xl"
              />
              <div className="absolute -bottom-8 -left-8 bg-card border border-card-border p-6 rounded-xl shadow-xl max-w-xs">
                <div className="flex items-center gap-4 mb-2">
                  <div className="flex text-primary">
                    {[1,2,3,4,5].map(star => <Star key={star} className="w-4 h-4 fill-current" />)}
                  </div>
                  <span className="font-bold text-sm">5.0 Average</span>
                </div>
                <p className="text-sm text-muted-foreground font-medium">Trusted by hundreds of Winnipeg drivers.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Maintenance Plans */}
      <section id="maintenance-plans" className="py-24 bg-background">
        <div className="container mx-auto px-4 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="text-sm font-bold tracking-[0.2em] text-primary uppercase mb-3">{plans.sectionLabel}</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">{plans.sectionTitle}</h2>
            <p className="text-muted-foreground text-lg">{plans.sectionSubtitle}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 items-start max-w-6xl mx-auto">
            {plans.plans.map((plan) => {
              const PlanIcon = planIconMap[plan.icon];
              return (
                <div
                  key={plan.id}
                  className={`bg-card rounded-2xl p-8 flex flex-col h-full relative ${
                    plan.highlight
                      ? "border-2 border-primary"
                      : "border border-border"
                  }`}
                >
                  {plan.badge && (
                    <div className="absolute -top-4 left-0 right-0 flex justify-center">
                      <span className="bg-primary text-primary-foreground text-xs font-bold tracking-widest px-5 py-1.5 rounded-full uppercase whitespace-nowrap">
                        {plan.badge}
                      </span>
                    </div>
                  )}

                  <div className={`mb-6 ${plan.badge ? "mt-2" : ""}`}>
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                      <PlanIcon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-xl font-bold tracking-wide uppercase mb-1">{plan.name}</h3>
                    <p className="text-muted-foreground text-sm">{plan.description}</p>
                  </div>

                  <div className="mb-6">
                    <div className="flex items-baseline gap-1 flex-wrap">
                      <span className="text-primary text-2xl font-bold leading-none">$</span>
                      <span className="text-4xl md:text-5xl font-extrabold text-primary leading-none">{plan.price}</span>
                      {plan.priceSuffix && (
                        <span className="text-sm font-semibold text-muted-foreground leading-none">{plan.priceSuffix}</span>
                      )}
                    </div>
                    <p className="text-muted-foreground text-sm mt-1">{plan.priceNote}</p>
                    {plan.paymentNote && (
                      <p className="text-muted-foreground text-xs mt-1">{plan.paymentNote}</p>
                    )}
                  </div>

                  <hr className="border-border mb-6" />

                  <ul className="space-y-3 mb-8 flex-1">
                    {plan.items.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-sm">
                        <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>

                  <Button asChild className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold">
                    <Link href={`/book?plan=${encodeURIComponent(plan.name)}`}>{plan.buttonText}</Link>
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 8. Testimonials */}
      <section id="reviews" className="scroll-mt-24 py-24 bg-popover border-y border-border">
        <div className="container mx-auto px-4 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="text-sm font-bold tracking-[0.2em] text-primary uppercase mb-3">Real Local Reviews</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">Don't Just Take Our Word For It.</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-card border border-card-border p-8 rounded-xl shadow-sm">
              <div className="flex text-primary mb-6">
                {[1,2,3,4,5].map(star => <Star key={star} className="w-5 h-5 fill-current" />)}
              </div>
              <p className="text-lg mb-6 text-foreground italic">"I highly recommend Swift Mobile Mechanic. When I called on a Saturday evening for a battery change on my Dodge Journey GT, the front driver side tire had to be removed to get to the battery. It was completed in a few hours after I called. The price was very reasonable!"</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold text-sm">TA</div>
                <div>
                  <p className="font-bold text-sm">Tommy Andrews</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-card-border p-8 rounded-xl shadow-sm">
              <div className="flex text-primary mb-6">
                {[1,2,3,4,5].map(star => <Star key={star} className="w-5 h-5 fill-current" />)}
              </div>
              <p className="text-lg mb-6 text-foreground italic">"Paul couldn't be more of a gentleman! He definitely helped me out of a bind. My brakes started grinding a day before hauling my camper on a busy weekend. He was prompt and very cordial, and completed all 4 sets in under a few hours. The service and convenience factor is brilliant and appreciated. Highly recommended!"</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold text-sm">AM</div>
                <div>
                  <p className="font-bold text-sm">Alex Middleton</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-card-border p-8 rounded-xl shadow-sm">
              <div className="flex text-primary mb-6">
                {[1,2,3,4,5].map(star => <Star key={star} className="w-5 h-5 fill-current" />)}
              </div>
              <p className="text-lg mb-6 text-foreground italic">"Paul came to my house to fix my car. I found him very polite, honest, and reliable. He fixed the car in the time he said. I would recommend him to anyone."</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold text-sm">PR</div>
                <div>
                  <p className="font-bold text-sm">Pam Rodych</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <a
              href="https://www.google.com/search?client=safari&hl=en-ca&hs=yBM&kgmid=%2Fg%2F11yc2tl634&kgs=3c4b9696e6b97ec3&q=Swift+Mobile+Mechanic&sca_esv=0baaa2d5d8a71e57&shndl=30&source=sh%2Fx%2Floc%2Fact%2Fm1%2F3&sxsrf=ANbL-n5XELFGChDwTwtv4c_csYqeKo9vcQ%3A1775933862594"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-7 py-3.5 rounded-full border border-border bg-card hover:border-primary/60 hover:shadow-lg hover:shadow-primary/10 transition-all group"
            >
              <span className="flex">
                {[1,2,3,4,5].map(star => <Star key={star} className="w-4 h-4 fill-current text-primary" />)}
              </span>
              <span className="font-semibold text-foreground text-sm tracking-wide">Read Our Google Reviews</span>
              <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
            </a>
          </div>
        </div>
      </section>

      {/* Photo Strip — visual proof between reviews and FAQ */}
      <section className="py-0 bg-background overflow-hidden">
        <div className="container mx-auto px-4 md:px-8 py-12 md:py-16">
          <div className="grid grid-cols-3 gap-3 md:gap-4">
            <div className="aspect-[4/3] overflow-hidden rounded-xl">
              <img
                src={imgs.brakeRotorOptimized || imgs.brakeRotor}
                alt="New brake rotor installed by Swift Mobile Mechanic on-site"
                loading="lazy"
                width={600}
                height={450}
                className="w-full h-full object-cover hover:scale-105 transition-all duration-700"
              />
            </div>
            <div className="aspect-[4/3] overflow-hidden rounded-xl">
              <img
                src={imgs.vanWinterOptimized || imgs.vanWinter}
                alt="Swift Mobile Mechanic providing emergency roadside service in Winnipeg winter"
                loading="lazy"
                width={600}
                height={450}
                className="w-full h-full object-cover hover:scale-105 transition-all duration-700"
              />
            </div>
            <div className="aspect-[4/3] overflow-hidden rounded-xl">
              <img
                src={imgs.suspensionOptimized || imgs.suspension}
                alt="Suspension repair with professional tools by Swift Mobile Mechanic"
                loading="lazy"
                width={600}
                height={450}
                className="w-full h-full object-cover hover:scale-105 transition-all duration-700"
              />
            </div>
          </div>
          <p className="text-center text-xs text-muted-foreground mt-4 tracking-wider uppercase font-medium">Real work. Real tools. Your driveway.</p>
        </div>
      </section>

      {/* 9. FAQ / 10. Contact Split Section */}
      <section id="contact" className="scroll-mt-24 py-24 bg-background">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
            
            {/* FAQ Side */}
            <div>
              <p className="text-sm font-bold tracking-[0.2em] text-primary uppercase mb-3">Common Questions</p>
              <h2 className="text-3xl font-bold tracking-tight mb-8">What You Need to Know.</h2>
              
              <div className="w-full border-t border-border">
                {[
                  {
                    question: "Do you offer warranty on your services?",
                    answer: "Yes. We provide a 30-day limited warranty on labour for the specific repair or service performed. Parts are covered only by the manufacturer's or supplier's warranty, if applicable.",
                  },
                  {
                    question: "What areas do you service?",
                    answer: "We service Winnipeg and the surrounding area. Service calls within Winnipeg are charged at our standard rate. Locations outside Winnipeg city limits may be subject to additional travel fees.",
                  },
                  {
                    question: "Can I schedule a same-day appointment?",
                    answer: "We accommodate same-day emergency repairs whenever possible, depending on parts availability and current scheduling. Call us directly for immediate assistance.",
                  },
                  {
                    question: "Do you charge a mobile service fee?",
                    answer: "There is a standard dispatch fee to cover travel and initial diagnostic time, which is completely transparent and quoted before we arrive. No surprises.",
                  },
                ].map((item) => (
                  <details key={item.question} className="group border-b border-border">
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-5 text-left text-lg font-bold hover:text-primary">
                      {item.question}
                      <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground transition-transform group-open:rotate-90" />
                    </summary>
                    <p className="pb-5 pr-8 text-base leading-relaxed text-muted-foreground">{item.answer}</p>
                  </details>
                ))}
              </div>
            </div>

            {/* Form Side */}
            <div className="bg-popover border border-border p-8 md:p-10 rounded-2xl shadow-xl">
              {isSuccess ? (
                <div className="text-center py-6">
                  <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center text-primary mx-auto mb-5">
                    <CheckCircle2 className="w-8 h-8" />
                  </div>
                  <h2 className="text-xl font-bold mb-2">Request Received!</h2>
                  <p className="text-muted-foreground text-sm mb-6">We'll review your details and follow up shortly to confirm availability and provide a quote.</p>
                  <Button onClick={() => setIsSuccess(false)} variant="outline" size="sm">Submit Another Request</Button>
                </div>
              ) : (
                <>
                  <h2 className="text-2xl font-bold tracking-tight mb-2">Request a Quote</h2>
                  <p className="text-muted-foreground mb-8">Tell us what's going on with your vehicle. We'll get back to you fast.</p>

                  <form onSubmit={onSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label htmlFor="quote-name" className="text-sm font-medium text-muted-foreground">Full Name</label>
                        <Input id="quote-name" name="name" autoComplete="name" placeholder="John Doe" minLength={2} required className="bg-background border-input focus-visible:ring-primary" />
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="quote-phone" className="text-sm font-medium text-muted-foreground">Phone Number</label>
                        <Input id="quote-phone" name="phone" type="tel" autoComplete="tel" inputMode="tel" placeholder="431 688-4950" minLength={10} required className="bg-background border-input focus-visible:ring-primary" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label htmlFor="quote-location" className="text-sm font-medium text-muted-foreground">Location</label>
                        <Input id="quote-location" name="location" autoComplete="street-address" placeholder="Address or Area" minLength={5} required className="bg-background border-input focus-visible:ring-primary" />
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="quote-vehicle" className="text-sm font-medium text-muted-foreground">Vehicle Details</label>
                        <Input id="quote-vehicle" name="vehicle" placeholder="Year / Make / Model" minLength={4} required className="bg-background border-input focus-visible:ring-primary" />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="quote-issue" className="text-sm font-medium text-muted-foreground">Describe the Issue</label>
                      <Textarea
                        id="quote-issue"
                        name="issue"
                        placeholder="Check engine light is on, brakes are grinding, won't start, etc..."
                        minLength={10}
                        required
                        className="bg-background border-input focus-visible:ring-primary min-h-[100px] resize-none"
                      />
                    </div>

                      {hasError && (
                        <div className="flex items-start gap-3 bg-destructive/10 border border-destructive/20 text-destructive rounded-lg p-4 text-sm">
                          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
                          <p>Something went wrong. Please try again or call <a href={`tel:${cfg.phone.tel}`} className="underline font-medium">{cfg.phone.display}</a>.</p>
                        </div>
                      )}

                      <Button type="submit" size="lg" disabled={isSubmitting} className="w-full text-base font-bold h-14">
                        {isSubmitting ? <><Loader2 className="mr-2 w-5 h-5 animate-spin" />Sending…</> : "Send Request"}
                      </Button>
                  </form>
                </>
              )}
            </div>

          </div>
        </div>
      </section>

      {/* 11. Final CTA */}
      <section className="py-24 bg-primary relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white via-transparent to-transparent" />
        <div className="container mx-auto px-4 md:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-primary-foreground mb-6">Need a Mechanic Without the Shop Hassle?</h2>
          <p className="text-xl text-primary-foreground/90 mb-10 max-w-2xl mx-auto font-medium">We're ready to get you back on the road. Fast, professional, and right at your doorstep.</p>
          <Button onClick={() => scrollToSection('contact')} variant="secondary" size="lg" className="h-14 px-10 text-lg font-bold bg-background text-foreground hover:bg-popover hover:text-primary">
            Book Mobile Service Now
          </Button>
        </div>
      </section>

      {/* 12. Footer */}
      <footer className="bg-background border-t border-border py-12 md:py-16">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-8 mb-12">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-6 h-6 rounded bg-primary flex items-center justify-center">
                  <Wrench className="w-3.5 h-3.5 text-primary-foreground" />
                </div>
                <span className="font-bold text-lg tracking-tight uppercase">Swift <span className="text-primary">Mobile Mechanic</span></span>
              </div>
              <p className="text-primary font-semibold text-sm tracking-wide mb-3">Your Garage on Wheels — We Come to You</p>
              <p className="text-muted-foreground max-w-sm mb-6 leading-relaxed">
                Winnipeg's mobile mechanic service. Professional diagnostics and repair delivered to your home, workplace, or roadside.
              </p>
              <div className="flex items-center gap-4 text-muted-foreground font-medium">
                <a href={`tel:${cfg.phone.tel}`} className="flex items-center gap-2 hover:text-primary transition-colors"><Phone className="w-4 h-4 text-primary" /> {cfg.phone.display}</a>
              </div>
            </div>
            
            <div>
              <h2 className="font-bold text-foreground mb-6 uppercase tracking-wider text-sm">Services</h2>
              <ul className="space-y-3 text-muted-foreground">
                <li><Link href="/services" className="hover:text-primary transition-colors">Diagnostics</Link></li>
                <li><Link href="/services" className="hover:text-primary transition-colors">Brake Repair</Link></li>
                <li><Link href="/services" className="hover:text-primary transition-colors">Battery Services</Link></li>
                <li><Link href="/services" className="hover:text-primary transition-colors">Oil &amp; Maintenance</Link></li>
                <li><Link href="/services" className="hover:text-primary transition-colors">Pre-Purchase Inspections</Link></li>
              </ul>
            </div>
            
            <div>
              <h2 className="font-bold text-foreground mb-6 uppercase tracking-wider text-sm">Company</h2>
              <ul className="space-y-3 text-muted-foreground">
                <li><Link href="/about" className="hover:text-primary transition-colors">About Us</Link></li>
                <li><Link href="/book" className="hover:text-primary transition-colors">Book Appointment</Link></li>
                <li><Link href="/contact" className="hover:text-primary transition-colors">Contact Us</Link></li>
                <li>
                  <a
                    href={cfg.whatsapp.bookingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-primary transition-colors"
                  >
                    WhatsApp Us
                  </a>
                </li>
                <li><Link href="/privacy" className="hover:text-primary transition-colors">Privacy Policy</Link></li>
                <li><Link href="/warranty" className="hover:text-primary transition-colors">Warranty Policy</Link></li>
                <li>
                  <a
                    href="https://www.google.com/search?client=safari&hl=en-ca&hs=yBM&kgmid=%2Fg%2F11yc2tl634&kgs=3c4b9696e6b97ec3&q=Swift+Mobile+Mechanic&sca_esv=0baaa2d5d8a71e57&shndl=30&source=sh%2Fx%2Floc%2Fact%2Fm1%2F3&sxsrf=ANbL-n5XELFGChDwTwtv4c_csYqeKo9vcQ%3A1775933862594"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-primary transition-colors flex items-center gap-1.5"
                  >
                    <Star className="w-3.5 h-3.5 text-primary fill-current" /> Google Reviews
                  </a>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="pt-8 border-t border-border flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-muted-foreground">
            <p>&copy; {new Date().getFullYear()} Swift Mobile Mechanic. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <Link href="/privacy" className="hover:text-primary transition-colors">Privacy Policy</Link>
              <Link href="/warranty" className="hover:text-primary transition-colors">Warranty Policy</Link>
              <p className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Proudly Serving Winnipeg, MB</p>
            </div>
          </div>
        </div>
      </footer>
      <WhatsAppButton />
    </div>
  );
}
